import ErrorBoundary from "components/error-boundary";
import { useCallback, useEffect, useRef, useState } from "react";

import { PDFDocumentProxy } from "pdfjs-dist";
import { PDFViewer } from "pdfjs-dist/web/pdf_viewer.mjs";

import * as pdfjsViewer from "pdfjs-dist/web/pdf_viewer.mjs";
import { ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { getPdfLoader } from "./loader";
import "./index.scss";
import "pdfjs-dist/web/pdf_viewer.css";
import PdfLoaderDiv from "./pdf-loader";
import { BoundingBoxPositionType } from "./models";

/** PDF.js PageViewport fields used for bbox scaling (rawDims matches setLayerDimensions). */
export type PdfViewportLike = {
  rotation?: number;
  rawDims: {
    pageWidth: number;
    pageHeight: number;
  };
};

const ZOOM_WHEEL_SENSITIVITY = 0.002;
const ZOOM_DEBOUNCE_MS = 200;
const MIN_SCALE = 0.25;
const MAX_SCALE = 10;

type PageChangeType = {
  pageLabel: any;
  pageNumber: number;
  previous: number | null;
};

const deferDoubleAnimationFrame = (callback: () => void): void => {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
};

const mergePageChange = <T extends { pageChange?: PageChangeType }>(
  prev: T,
  pageChange: PageChangeType
): T => ({ ...prev, pageChange });

/**
 * Maps a bbox in spec-page space (top-left origin) into the PDF.js text layer box.
 * Uses viewport.rawDims pageWidth/pageHeight so scaling matches PDF.js layer CSS
 * (`var(--scale-factor) * pageWidth`), not only API width/height fields.
 * Corrects for `round(down, …)` layer sizes: nominal size from viewport vs clientWidth/Height.
 */
export function bboxToTextLayerCssPixels(
  bbox: BoundingBoxPositionType,
  textLayerDiv: { clientWidth: number; clientHeight: number },
  rotation: number,
  viewport?: PdfViewportLike | null
): { left: number; top: number; width: number; height: number } {
  const tw = textLayerDiv.clientWidth;
  const th = textLayerDiv.clientHeight;
  const pw = viewport?.rawDims?.pageWidth ?? bbox.width;
  const ph = viewport?.rawDims?.pageHeight ?? bbox.height;
  const { x1, x2, y1, y2 } = bbox;
  const r = ((rotation % 360) + 360) % 360;

  let left: number;
  let top: number;
  let width: number;
  let height: number;

  switch (r) {
    case 90: {
      const sx = tw / ph;
      const sy = th / pw;
      left = y1 * sx;
      top = x1 * sy;
      width = (y2 - y1) * sx;
      height = (x2 - x1) * sy;
      break;
    }
    case 180: {
      const sx = tw / pw;
      const sy = th / ph;
      left = (pw - x2) * sx;
      top = (ph - y2) * sy;
      width = (x2 - x1) * sx;
      height = (y2 - y1) * sy;
      break;
    }
    case 270: {
      const sx = tw / ph;
      const sy = th / pw;
      left = (ph - y2) * sx;
      top = (pw - x2) * sy;
      width = (y2 - y1) * sx;
      height = (x2 - x1) * sy;
      break;
    }
    default: {
      const sx = tw / pw;
      const sy = th / ph;
      left = x1 * sx;
      top = y1 * sy;
      width = (x2 - x1) * sx;
      height = (y2 - y1) * sy;
    }
  }

  const roundPx = (v: number) => Math.max(0, Math.round(v));

  return {
    left: roundPx(left),
    top: roundPx(top),
    width: roundPx(width),
    height: roundPx(height)
  };
}

function CiqPdfViewer({
  fileUrl,
  onExpiredUrl,
  boundingBoxPossition
}: Readonly<{
  fileUrl: string;
  onExpiredUrl: () => void;
  boundingBoxPossition?: BoundingBoxPositionType;
}>) {
  const viewerContainerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const boundingBoxRef = useRef(boundingBoxPossition);
  boundingBoxRef.current = boundingBoxPossition;
  // Read through a ref so a new callback identity from the parent never
  // re-runs the load effect (which tears down and reloads the whole PDF).
  const onExpiredUrlRef = useRef(onExpiredUrl);
  onExpiredUrlRef.current = onExpiredUrl;
  const [viewerPdf, setViewer] = useState<PDFViewer>();
  const [loading, setLoading] = useState(false);
  const [percent, setPercent] = useState<number>(0);

  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  const zoomTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingScaleRef = useRef<number | null>(null);
  const zoomOriginScaleRef = useRef<number>(1);
  const zoomMouseRef = useRef({ x: 0, y: 0 });
  const zoomScrollRef = useRef({ left: 0, top: 0 });
  const zoomTransformOriginRef = useRef({ x: 0, y: 0 });

  const handlePageChanging = useCallback((pageChange: PageChangeType) => {
    setState((prev) => mergePageChange(prev, pageChange));
  }, []);

  const [, setState] = useState<{
    pageCount: number;
    pageChange: PageChangeType;
  }>({
    pageCount: 0,
    pageChange: {
      pageNumber: 1,
      previous: null,
      pageLabel: ""
    }
  });

  const appendCanvasLayer = useCallback(
    (
      textLayerDiv: HTMLDivElement,
      pageView: { viewport?: PdfViewportLike } | null
    ) => {
      const bb = boundingBoxRef.current;
      if (!bb) return;

      const vr = pageView?.viewport?.rotation;
      const rotation =
        typeof vr === "number" && !Number.isNaN(vr)
          ? vr
          : Number.parseInt(
              textLayerDiv.getAttribute("data-main-rotation") ?? "0",
              10
            ) || 0;

      const box = bboxToTextLayerCssPixels(
        bb,
        textLayerDiv,
        rotation,
        pageView?.viewport ?? null
      );
      const left = `${box.left}px`;
      const top = `${box.top}px`;
      const width = `${box.width}px`;
      const height = `${box.height}px`;

      const id = "canvas-text-layer-annotation";
      let rectangle = document.getElementById(id);
      rectangle ??= document.createElement("div");
      rectangle.id = id;
      rectangle.setAttribute(
        "class",
        "absolute pointer-events-none outline outline-ciq-green-600 outline-4"
      );
      rectangle.setAttribute("dir", "ltr");
      rectangle.style.width = width;
      rectangle.style.height = height;
      rectangle.style.left = left;
      rectangle.style.top = top;

      textLayerDiv.appendChild(rectangle);
    },
    []
  );

  useEffect(() => {
    if (!viewerPdf || !boundingBoxPossition) return;
    const pageIdx = Math.max(0, boundingBoxPossition.page_no ?? 0);
    const pv = viewerPdf.getPageView(pageIdx);
    if (pv?.textLayer?.div) {
      appendCanvasLayer(pv.textLayer.div, pv);
    }
  }, [appendCanvasLayer, boundingBoxPossition, viewerPdf]);

  useEffect(() => {
    let pdfViewer: PDFViewer;
    let cancelled = false;
    let onScaleChange: (() => void) | undefined;
    let onPagesInit: (() => void) | undefined;
    let onTextLayerRendered: ((e: any) => void) | undefined;
    const container = viewerContainerRef.current;

    const onProgress = (data: any) => {
      if (cancelled) return;
      const percentProgress = Math.round((data.loaded * 95) / data.total);
      setPercent(percentProgress);
    };

    const load = async () => {
      setPercent(0);
      setLoading(true);
      const pdfDocument: PDFDocumentProxy | null = await getPdfLoader({
        fileUrl,
        onExpiredUrl: () => onExpiredUrlRef.current(),
        onProgress
      });
      if (cancelled) {
        // The effect was cleaned up while loading: don't attach a second
        // viewer to the same container.
        pdfDocument?.destroy();
        return;
      }
      if (pdfDocument === null) {
        setLoading(false);
        return;
      }

      if (pdfDocument && container && viewerRef.current) {
        setState((pre) => ({ ...pre, pageCount: pdfDocument.numPages }));
        const pdfEventBus = new pdfjsViewer.EventBus();
        const pdfLinkService = new pdfjsViewer.PDFLinkService({
          eventBus: pdfEventBus
        });

        pdfViewer = new pdfjsViewer.PDFViewer({
          container,
          viewer: viewerRef.current,
          linkService: pdfLinkService,
          eventBus: pdfEventBus,
          removePageBorders: true
        } as any);

        pdfLinkService.setDocument(pdfDocument, null);
        pdfLinkService.setViewer(pdfViewer);

        pdfViewer.setDocument(pdfDocument);
        setViewer(pdfViewer);

        // Events
        onPagesInit = () => {
          // We can use pdfViewer now, e.g. let's change default scale.
          pdfViewer.currentScaleValue = "page-width";
          setPercent(100);
          setLoading(false);
        };
        pdfViewer.eventBus.on("pagesinit", onPagesInit);

        const refreshBoundingOverlay = () => {
          const bb = boundingBoxRef.current;
          if (!bb) return;
          const pageIdx = Math.max(0, bb.page_no ?? 0);
          const pv = pdfViewer.getPageView(pageIdx);
          if (pv?.textLayer?.div) {
            appendCanvasLayer(pv.textLayer.div, pv);
          }
        };

        onTextLayerRendered = (e: any) => {
          const bb = boundingBoxRef.current;
          if (!bb) return;
          const targetIdx = Math.max(0, bb.page_no ?? 0);
          if (e.pageNumber - 1 !== targetIdx) return;
          const pageView = pdfViewer.getPageView(e.pageNumber - 1);
          if (pageView?.textLayer?.div) {
            appendCanvasLayer(pageView.textLayer.div, pageView);
          }
        };
        pdfViewer.eventBus.on("textlayerrendered", onTextLayerRendered);

        onScaleChange = () => {
          deferDoubleAnimationFrame(refreshBoundingOverlay);
        };
        pdfViewer.eventBus.on("scalechanging", onScaleChange);

        pdfViewer.eventBus.on("pagechanging", handlePageChanging);
      }
    };
    load();

    return () => {
      cancelled = true;
      if (pdfViewer) {
        if (pdfViewer.pdfDocument) {
          pdfViewer.pdfDocument.destroy();
          pdfViewer.pdfDocument.cleanup();
        }
        if (pdfViewer.eventBus) {
          if (onScaleChange) {
            pdfViewer.eventBus.off("scalechanging", onScaleChange);
          }
          if (onTextLayerRendered) {
            pdfViewer.eventBus.off("textlayerrendered", onTextLayerRendered);
          }
          if (onPagesInit) {
            pdfViewer.eventBus.off("pagesinit", onPagesInit);
          }
          pdfViewer.eventBus.off("pagechanging", handlePageChanging);
        }
        pdfViewer.cleanup();
      }
    };
  }, [appendCanvasLayer, fileUrl, handlePageChanging]);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('a, button, input, [role="button"]')) return;

    const container = viewerContainerRef.current;
    if (!container) return;

    isDraggingRef.current = true;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop
    };
    container.classList.add("is-grabbing");
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    const container = viewerContainerRef.current;
    if (!container) return;

    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    container.scrollLeft = dragStartRef.current.scrollLeft - dx;
    container.scrollTop = dragStartRef.current.scrollTop - dy;
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    viewerContainerRef.current?.classList.remove("is-grabbing");
  }, []);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      if (!viewerPdf) return;

      const container = viewerContainerRef.current;
      const viewer = viewerRef.current;
      if (!container || !viewer) return;

      const currentScale = pendingScaleRef.current ?? viewerPdf.currentScale;

      if (pendingScaleRef.current === null) {
        zoomOriginScaleRef.current = viewerPdf.currentScale;

        const containerRect = container.getBoundingClientRect();
        zoomMouseRef.current = {
          x: e.clientX - containerRect.left,
          y: e.clientY - containerRect.top
        };
        zoomScrollRef.current = {
          left: container.scrollLeft,
          top: container.scrollTop
        };

        const viewerRect = viewer.getBoundingClientRect();
        zoomTransformOriginRef.current = {
          x: e.clientX - viewerRect.left,
          y: e.clientY - viewerRect.top
        };
      }

      const factor = Math.max(
        0.8,
        Math.min(1.25, 1 - e.deltaY * ZOOM_WHEEL_SENSITIVITY)
      );
      const newScale = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, currentScale * factor)
      );
      pendingScaleRef.current = newScale;

      const cssRatio = newScale / zoomOriginScaleRef.current;
      const { x: ox, y: oy } = zoomTransformOriginRef.current;
      viewer.style.transformOrigin = `${ox}px ${oy}px`;
      viewer.style.transform = `scale(${cssRatio})`;

      if (zoomTimeoutRef.current) clearTimeout(zoomTimeoutRef.current);
      zoomTimeoutRef.current = setTimeout(() => {
        if (pendingScaleRef.current !== null && viewerPdf) {
          const finalScale = pendingScaleRef.current;
          const originScale = zoomOriginScaleRef.current;
          const { x: mouseX, y: mouseY } = zoomMouseRef.current;
          const { left: scrollLeft, top: scrollTop } = zoomScrollRef.current;

          viewer.style.transform = "";
          viewer.style.transformOrigin = "";

          viewerPdf.currentScale = finalScale;

          const ratio = finalScale / originScale;
          container.scrollLeft = (scrollLeft + mouseX) * ratio - mouseX;
          container.scrollTop = (scrollTop + mouseY) * ratio - mouseY;

          pendingScaleRef.current = null;
        }
      }, ZOOM_DEBOUNCE_MS);
    },
    [viewerPdf]
  );

  useEffect(() => {
    const container = viewerContainerRef.current;

    if (container) {
      container.addEventListener("mousedown", handleMouseDown);
      container.addEventListener("wheel", handleWheel, { passive: false });
      globalThis.addEventListener("mousemove", handleMouseMove);
      globalThis.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      container?.removeEventListener("mousedown", handleMouseDown);
      container?.removeEventListener("wheel", handleWheel);
      globalThis.removeEventListener("mousemove", handleMouseMove);
      globalThis.removeEventListener("mouseup", handleMouseUp);
      if (zoomTimeoutRef.current) clearTimeout(zoomTimeoutRef.current);
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp, handleWheel]);

  const zoomButtonsToolbar = (
    <div
      className="flex rounded-lg space-x-4 absolute left-3 bottom-3 p-3 bg-ciq-primary-200 border border-solid "
      style={{ zIndex: 100 }}
    >
      <ZoomOutOutlined
        size={20}
        className="text-white"
        onClick={() => {
          if (viewerPdf) {
            viewerPdf.decreaseScale({ steps: 1 });
          }
        }}
      />

      <ZoomInOutlined
        size={20}
        className="text-white"
        onClick={() => {
          if (viewerPdf) {
            viewerPdf.increaseScale({ steps: 1 });
          }
        }}
      />
    </div>
  );

  return (
    <ErrorBoundary>
      <div className="relative pdf-viewer-ciq bg-gray-400 z-10 w-full h-[calc(100%-28px)] justify-center items-center overflow-auto">
        <div className="viewerContainer" ref={viewerContainerRef}>
          <div id="viewer" ref={viewerRef} className="pdfViewer" />
        </div>
        {loading ? <PdfLoaderDiv percent={percent} /> : zoomButtonsToolbar}
      </div>
    </ErrorBoundary>
  );
}
export default CiqPdfViewer;
